import { useCallback, useEffect, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer,
} from "recharts";
import {
  TrendUp, CircleNotch, PencilSimple, Sun, Warning,
  CloudRain, Sparkle, CheckCircle, PiggyBank, Plus, Trash, BellRinging,
  Receipt, CalendarPlus, HourglassMedium, RocketLaunch, Trophy, Target, ArrowCounterClockwise,
  ShareNetwork,
} from "@phosphor-icons/react";
import { RowanTerm } from "@/components/RowanTerm";
import { MilestoneShareCard } from "@/components/MilestoneShareCard";
import { SetBankBalanceDialog } from "@/components/SetBankBalanceDialog";

const money = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return v < 0 ? `-$${s}` : `$${s}`;
};
const money2 = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `-$${s}` : `$${s}`;
};
const dayLabel = (iso) =>
  new Date(iso + "T00:00:00").toLocaleString("en-US", { month: "short", day: "numeric" });

const STATUS = {
  green: {
    label: "Healthy",
    color: "var(--bk-success)",
    bg: "var(--bk-success-bg)",
    icon: Sun,
    caption: "Comfortable cushion ahead",
  },
  yellow: {
    label: "Tight",
    color: "#B45309",
    bg: "#FEF3C7",
    icon: Warning,
    caption: "Covered, but keep it lean",
  },
  red: {
    label: "Shortfall risk",
    color: "var(--bk-energy)",
    bg: "var(--bk-energy-bg)",
    icon: CloudRain,
    caption: "Projected to run short",
  },
};

const WINDOWS = [30, 60, 90];

function Stat({ label, value, sub, color, term }) {
  return (
    <div className="bk-card p-4 bk-lift" data-testid={`forecast-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--bk-text-2)" }}>{term ? <RowanTerm term={term} size={12}>{label}</RowanTerm> : label}</p>
      <p className="font-display text-xl font-bold" style={{ color: color || "var(--bk-text)" }}>{value}</p>
      {sub && <p className="text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>{sub}</p>}
    </div>
  );
}

export function CashFlowForecast({ businessId, businessName }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [bills, setBills] = useState([]);
  const [showBill, setShowBill] = useState(false);
  const [billForm, setBillForm] = useState({ name: "", amount: "", due_date: "", category: "", recurring: false });
  const [savingBill, setSavingBill] = useState(false);
  const [reminding, setReminding] = useState("");
  const [history, setHistory] = useState(null);
  const [goalEditing, setGoalEditing] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [shareCard, setShareCard] = useState(null); // milestone number | null

  const saveGoal = async () => {
    const g = parseInt(goalInput, 10);
    if (Number.isNaN(g) || g < 0) { toast.error("Enter a number of days (or 0 to clear)."); return; }
    try {
      await api.put(`/businesses/${businessId}/runway-goal`, { goal_days: g });
      setGoalEditing(false);
      const { data } = await api.get(`/businesses/${businessId}/forecast`, {
        params: { days },
      });
      setData(data);
      if (g <= 0) toast.success("Runway goal cleared");
      else if (data.goal_met) toast.success(`You're already past your ${g}-day goal — outstanding!`);
      else toast.success(`Runway goal set to ${g} days — let's get you there`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save that goal.");
    }
  };

  const load = useCallback(async (win) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/businesses/${businessId}/forecast`, { params: { days: win } });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load your forecast.");
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  const loadBills = useCallback(async () => {
    try {
      const { data } = await api.get(`/businesses/${businessId}/scheduled-bills`, { params: { include_paid: true } });
      setBills(data || []);
    } catch { /* non-blocking */ }
  }, [businessId]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/businesses/${businessId}/forecast/runway-history`, { params: { months: 6 } });
      setHistory(data);
    } catch { /* non-blocking */ }
  }, [businessId]);

  const checkMilestone = useCallback(async () => {
    try {
      const { data } = await api.post(`/businesses/${businessId}/forecast/check-milestone`);
      if (data?.celebrate) {
        toast.success(`Milestone earned — ${data.milestone === 90 ? "90+" : data.milestone} days of projected runway.`, {
          description: data.label, duration: 10000,
          action: { label: "Share your win", onClick: () => setShareCard(data.milestone) },
        });
      }
    } catch { /* non-blocking */ }
  }, [businessId]);

  useEffect(() => { load(days); }, [days, load]);
  useEffect(() => { loadBills(); loadHistory(); checkMilestone(); }, [loadBills, loadHistory, checkMilestone]);

  const addBill = async () => {
    const amt = parseFloat(billForm.amount);
    if (!billForm.name.trim()) { toast.error("Name the bill (e.g. Q3 taxes)."); return; }
    if (Number.isNaN(amt) || amt <= 0) { toast.error("Enter an amount."); return; }
    if (!billForm.due_date) { toast.error("Pick a due date."); return; }
    setSavingBill(true);
    try {
      await api.post(`/businesses/${businessId}/scheduled-bills`, {
        name: billForm.name.trim(), amount: amt, due_date: billForm.due_date, category: billForm.category.trim(),
        recurring: billForm.recurring,
      });
      toast.success(billForm.recurring ? "Recurring bill added to your forecast" : "Bill added to your forecast");
      setBillForm({ name: "", amount: "", due_date: "", category: "", recurring: false });
      setShowBill(false);
      await Promise.all([loadBills(), load(days)]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't add that bill.");
    } finally { setSavingBill(false); }
  };

  const deleteBill = async (bl) => {
    const msg = bl.recurring
      ? `Delete the recurring bill "${bl.name}" (${money2(bl.amount)}/mo)? It stops appearing in your forecast from now on. Payments you already logged to Books stay put.`
      : `Delete the bill "${bl.name}" (${money2(bl.amount)})? It will be removed from your forecast.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/scheduled-bills/${bl.id}`);
      toast.success(`Deleted "${bl.name}"${bl.recurring ? " — no more monthly entries" : ""}.`);
      await Promise.all([loadBills(), load(days)]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't remove that bill."); }
  };

  const [editBill, setEditBill] = useState(null); // {id, name, amount, due_date, category, recurring}
  const [savingEdit, setSavingEdit] = useState(false);
  const saveBillEdit = async () => {
    const amt = parseFloat(editBill.amount);
    if (!editBill.name.trim()) { toast.error("Name the bill."); return; }
    if (Number.isNaN(amt) || amt <= 0) { toast.error("Enter an amount."); return; }
    if (!editBill.due_date) { toast.error("Pick a date."); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/scheduled-bills/${editBill.id}`, { name: editBill.name.trim(), amount: amt, due_date: editBill.due_date, category: editBill.category.trim(), recurring: editBill.recurring });
      toast.success(`Updated "${editBill.name.trim()}"`);
      setEditBill(null);
      await Promise.all([loadBills(), load(days)]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't update that bill."); }
    finally { setSavingEdit(false); }
  };

  const markBillPaid = async (bl) => {
    try {
      const { data } = await api.patch(`/scheduled-bills/${bl.id}`, { paid: true, log_to_books: true });
      toast.success(
        data?.logged
          ? `Marked "${bl.name}" paid and logged ${money2(bl.amount)} to Books`
          : `Marked "${bl.name}" paid — removed from your forecast`
      );
      await Promise.all([loadBills(), load(days), loadHistory()]);
    } catch { toast.error("Couldn't mark that bill paid."); }
  };

  const payRecurring = async (bl) => {
    try {
      await api.patch(`/scheduled-bills/${bl.id}`, { pay_occurrence: true });
      toast.success(`Logged ${money2(bl.amount)} for "${bl.name}" to Books — rolled to next month`);
      await Promise.all([loadBills(), load(days), loadHistory()]);
    } catch { toast.error("Couldn't pay that bill."); }
  };

  const undoPayment = async (bl) => {
    try {
      await api.post(`/scheduled-bills/${bl.id}/undo-payment`);
      toast.success(`Undid the payment for "${bl.name}" — pulled it back out of Books`);
      await Promise.all([loadBills(), load(days), loadHistory()]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Nothing to undo.");
    }
  };

  const nudge = async (inv) => {
    setReminding(inv.id);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/reminder`, { origin_url: window.location.origin });
      await navigator.clipboard?.writeText(data.message)?.catch(() => {});
      toast.success("Witty reminder copied — paste it to your client", { description: data.message });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't write that reminder.");
    } finally { setReminding(""); }
  };


  const st = STATUS[data?.status] || STATUS.green;
  const StIcon = st.icon;
  const negative = (data?.points || []).some((p) => p.balance < 0);

  return (
    <div className="space-y-6" data-testid="cash-flow-forecast">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
            <TrendUp size={24} weight="duotone" color="var(--bk-accent)" /> Cash Flow Forecast
          </h2>
          <p className="text-sm mt-0.5" style={{ color: "var(--bk-text-2)" }}>
            Where your cash is projected to go over the next {days} days — an estimate from your recent run-rate, not a promise.
          </p>
        </div>
        <div className="flex items-center gap-1 bk-card p-1 rounded-full" data-testid="forecast-window-toggle">
          {WINDOWS.map((w) => (
            <button
              key={w}
              data-testid={`forecast-window-${w}`}
              onClick={() => setDays(w)}
              className="px-3 py-1.5 rounded-full text-sm font-semibold transition-colors"
              style={days === w
                ? { background: "var(--bk-accent)", color: "#fff" }
                : { color: "var(--bk-text-2)" }}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="bk-card p-12 flex items-center justify-center gap-2" style={{ color: "var(--bk-text-2)" }}>
          <CircleNotch size={20} className="animate-spin" /> Reading your books…
        </div>
      ) : !data ? null : !data.has_history ? (
        <div className="bk-empty-state p-10 text-center" data-testid="forecast-empty">
          <PiggyBank size={40} weight="duotone" color="var(--bk-accent)" className="mx-auto mb-3" />
          <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Nothing here yet. Let&apos;s change that.</h3>
          <p className="text-sm mt-1 max-w-md mx-auto" style={{ color: "var(--bk-text-2)" }}>
            Log a few income and expense transactions in Books and I'll project your cash flow forward — with a little commentary.
          </p>
        </div>
      ) : (
        <>
          {/* Runway headline */}
          {(() => {
            const rw = data.runway_days;
            const positive = rw === null || rw === undefined;
            let color = "var(--bk-success)";
            if (!positive) {
              if (rw <= 30) color = "var(--bk-energy)";
              else if (rw <= 60) color = "#B45309";
            }
            const monthsLabel = !positive && rw >= 60 ? ` (~${Math.round(rw / 30)} months)` : "";
            return (
              <div
                className="rounded-[28px] p-6 flex items-center gap-5 text-white relative overflow-hidden shadow-[0_12px_36px_rgba(26,39,68,0.18)]"
                style={{ background: "var(--bk-gradient-primary)" }}
                data-testid="forecast-runway"
              >
                <div className="absolute -top-12 -right-8 w-44 h-44 rounded-full" style={{ background: color, opacity: 0.14 }} />
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: color }}>
                  {positive ? <RocketLaunch size={28} weight="fill" color="#fff" /> : <HourglassMedium size={28} weight="fill" color="#fff" />}
                </div>
                <div className="relative">
                  <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "rgba(255,255,255,0.65)" }}>Cash runway</p>
                  {positive ? (
                    <p className="font-display text-3xl sm:text-4xl font-bold leading-tight" data-testid="runway-value" style={{ color: "#fff" }}>
                      12+ months of runway <span className="text-lg font-semibold" style={{ color: "rgba(255,255,255,0.7)" }}>(projected)</span>
                    </p>
                  ) : (
                    <p className="font-display text-3xl sm:text-4xl font-bold leading-tight" data-testid="runway-value" style={{ color: "#fff" }}>
                      {rw === 0 ? "Projected at $0 now" : <>~<span style={{ color }}>{rw}</span> day{rw === 1 ? "" : "s"} of runway <span className="text-lg font-semibold" style={{ color: "rgba(255,255,255,0.7)" }}>(projected)</span></>}
                    </p>
                  )}
                  <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.75)" }}>
                    {positive
                      ? `At the pace of the last ${data.assumptions?.run_rate_window_days || 90} days, the projection doesn't reach $0 within a year. It moves as your books move.`
                      : (rw === 0
                          ? "Your books show the balance at or below $0 today — chase receivables and trim spend now."
                          : `Estimated from your recent pace of ${money(Math.abs(data.daily_net))}/day net ${data.daily_net < 0 ? "burn" : "gain"}, bills included${monthsLabel}. Log transactions regularly to keep it sharp.`)}
                  </p>

                  {/* Runway goal */}
                  <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.14)" }} data-testid="runway-goal">
                    {goalEditing ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>Target runway (days):</span>
                        <input type="number" value={goalInput} onChange={(e) => setGoalInput(e.target.value)}
                          data-testid="runway-goal-input" autoFocus placeholder="90"
                          className="w-20 px-2 py-1 rounded-md text-sm text-white bg-white/10 border border-white/25 focus:outline-none" />
                        <button onClick={saveGoal} data-testid="runway-goal-save"
                          className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: "#fff", color: "var(--bk-hero-bg, #0B0F19)" }}>Save</button>
                        <button onClick={() => setGoalEditing(false)} className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>Cancel</button>
                      </div>
                    ) : data.runway_goal_days ? (
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                        {data.goal_met ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full" data-testid="runway-goal-met"
                            style={{ background: "var(--bk-success)", color: "#fff" }}>
                            <Trophy size={13} weight="fill" /> Goal met on the projection — {data.runway_goal_days} days
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: "rgba(255,255,255,0.82)" }}>
                            <Target size={14} weight="bold" /> Goal: {data.runway_goal_days} days
                            {!positive && <span style={{ color: "rgba(255,255,255,0.55)" }}> · {Math.max(0, data.runway_goal_days - rw)} to go</span>}
                          </span>
                        )}
                        <button onClick={() => { setGoalInput(String(data.runway_goal_days)); setGoalEditing(true); }}
                          data-testid="runway-goal-edit" className="text-xs underline" style={{ color: "rgba(255,255,255,0.6)" }}>edit</button>
                      </div>
                    ) : (
                      <button onClick={() => { setGoalInput("90"); setGoalEditing(true); }} data-testid="runway-goal-set"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: "rgba(255,255,255,0.82)" }}>
                        <Target size={14} weight="bold" /> Set a runway goal
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Ledger's note */}
          <div
            className="bk-rowan-card rounded-[24px] p-5 flex items-start gap-3 border"
            style={{ background: "var(--bk-rowan-bg)", borderColor: "rgba(232,168,56,0.22)" }}
            data-testid="forecast-note"
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
              style={{ background: st.color }}
            >
              <StIcon size={22} weight="fill" color="#fff" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--bk-gold)" }}>
                  {st.label}
                </span>
                <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>· Rowan says</span>
              </div>
              <p className="text-base font-medium leading-snug" style={{ color: "var(--bk-text)" }} data-testid="forecast-note-text">
                {data.note?.message}
              </p>
            </div>
          </div>

          {/* Invoice Nudge — chase unpaid invoices that would fix a projected gap */}
          {data.status !== "green" && (data.open_invoices || []).length > 0 && (() => {
            const gap = data.shortfall_gap || 0;
            let acc = 0; const fixIds = new Set();
            for (const inv of data.open_invoices) { if (acc >= gap) break; acc += inv.total; fixIds.add(inv.id); }
            const covers = gap > 0 && acc >= gap;
            return (
              <div className="bk-card p-5 bk-lift" data-testid="invoice-nudge">
                <div className="flex items-start gap-2 mb-1">
                  <BellRinging size={20} weight="duotone" color="var(--bk-energy)" className="mt-0.5" />
                  <div>
                    <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Chase these invoices to close the gap</h3>
                    <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>
                      {gap > 0
                        ? (covers
                            ? <>Collecting the {fixIds.size} highlighted invoice{fixIds.size === 1 ? "" : "s"} ({money2(acc)}) covers your projected shortfall of {money2(gap)}.</>
                            : <>You're owed {money2((data.open_invoices || []).reduce((a, i) => a + i.total, 0))} total — collecting it all still leaves a gap, but every payment helps.</>)
                        : <>Money you're still owed. Collecting it early builds your cushion.</>}
                    </p>
                  </div>
                </div>
                <div className="mt-3 divide-y" style={{ borderColor: "var(--bk-border)" }}>
                  {(data.open_invoices || []).slice(0, 6).map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`nudge-invoice-${inv.id}`}
                      style={fixIds.has(inv.id) ? { background: "var(--bk-success-bg)", borderRadius: 8, paddingLeft: 8, paddingRight: 8 } : {}}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: "var(--bk-text)" }}>
                          {inv.client_name || "Client"} <span className="font-normal" style={{ color: "var(--bk-text-2)" }}>· {inv.number}</span>
                        </p>
                        <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>
                          {money2(inv.total)}{inv.due_date ? ` · due ${dayLabel(inv.due_date)}` : ""}{fixIds.has(inv.id) ? " · closes the gap" : ""}
                        </p>
                      </div>
                      <button
                        onClick={() => nudge(inv)}
                        disabled={reminding === inv.id}
                        data-testid={`nudge-send-${inv.id}`}
                        className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full disabled:opacity-60"
                        style={{ background: "var(--bk-energy)", color: "#fff" }}
                      >
                        {reminding === inv.id ? <CircleNotch size={13} className="animate-spin" /> : <BellRinging size={13} weight="bold" />}
                        Send reminder
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat
              label="Cash Today" term="cash_flow"
              value={money2(data.starting_balance)}
              color="var(--bk-text)"
              sub={
                <button
                  onClick={() => setBalanceDialog(true)}
                  className="inline-flex items-center gap-1 hover:underline"
                  style={{ color: "var(--bk-accent)" }}
                  data-testid="forecast-edit-balance"
                >
                  <PencilSimple size={12} /> {(data.cash_accounts || []).filter((a) => Math.abs(a.balance) >= 0.005).map((a) => `${a.name} ${money(a.balance)}`).join(" + ") || "From your books"} · Set bank balance
                </button>
              }
            />
            <Stat
              label={`Projected in ${data.days || days} days`}
              value={money2(data.end_balance)}
              color={data.end_balance < 0 ? "var(--bk-energy)" : "var(--bk-success)"}
              sub={`${data.net_change >= 0 ? "+" : ""}${money2(data.net_change)} projected change`}
            />
            <Stat
              label="Projected low" term="lowest_point"
              value={money2(data.lowest_balance)}
              color={data.lowest_balance < 0 ? "var(--bk-energy)" : (data.status === "yellow" ? "#B45309" : "var(--bk-text)")}
              sub={dayLabel(data.lowest_date)}
            />
            <Stat
              label="Invoices due" term="expected_receivables"
              value={money2(data.expected_receivables)}
              color="var(--bk-success)"
              sub={data.expected_receivables > 0 ? "sent & unpaid, due this window (drafts excluded)" : "no sent invoices due this window"}
            />
          </div>

          <SetBankBalanceDialog businessId={businessId} open={balanceDialog} onOpenChange={setBalanceDialog} onPosted={() => load(days)} />

          {/* Chart */}
          <div className="bk-card p-5 bk-lift" data-testid="forecast-chart">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Projected balance <span className="text-sm font-semibold" style={{ color: "var(--bk-text-2)" }}>(estimate)</span></h3>
              {loading && <CircleNotch size={16} className="animate-spin" style={{ color: "var(--bk-text-2)" }} />}
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={data.points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="fcFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={st.color} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={st.color} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tickFormatter={dayLabel}
                    tick={{ fontSize: 11, fill: "var(--bk-text-2)" }}
                    minTickGap={40}
                    axisLine={{ stroke: "var(--bk-border)" }}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={money}
                    tick={{ fontSize: 11, fill: "var(--bk-text-2)" }}
                    width={64}
                    axisLine={false}
                    tickLine={false}
                  />
                  {negative && <ReferenceLine y={0} stroke="var(--bk-energy)" strokeDasharray="4 4" />}
                  <Tooltip
                    formatter={(v) => [money2(v), "Balance"]}
                    labelFormatter={(l) => dayLabel(l)}
                    contentStyle={{ borderRadius: 12, border: "1px solid var(--bk-border)", fontSize: 13 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke={st.color}
                    strokeWidth={2.5}
                    fill="url(#fcFill)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-4 mt-4 text-xs" style={{ color: "var(--bk-text-2)" }}>
              <span className="flex items-center gap-1.5"><Sparkle size={13} weight="fill" color="var(--bk-gold)" /> Run-rate: {money2(data.avg_daily_income)}/day in, {money2(data.avg_daily_expense)}/day out (last {data.assumptions?.run_rate_window_days || 90} days)</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={13} weight="fill" color="var(--bk-success)" /> Plus {money2(data.expected_receivables)} in sent invoices due this window</span>
              <span className="flex items-center gap-1.5"><Receipt size={13} weight="fill" color="var(--bk-energy)" /> Minus {money2(data.scheduled_out || 0)} in bills due this window</span>
            </div>
            {data.assumptions && (() => {
              const a = data.assumptions;
              const shownDays = data.days || days;
              const runRate = a.daily_net * shownDays;
              const computedEnd = data.starting_balance + runRate + a.receivables_in_window - a.bills_in_window;
              const matchesHeadline = Math.abs(computedEnd - data.end_balance) < 1;
              return (
                <details className="mt-3 rounded-xl border px-4 py-3" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }} data-testid="forecast-method">
                  <summary className="text-sm font-semibold cursor-pointer select-none" style={{ color: "var(--bk-accent)" }}>How this projection is calculated</summary>
                  <ol className="mt-2 space-y-1.5 text-sm list-decimal pl-5" style={{ color: "var(--bk-text)" }} data-testid="forecast-method-steps">
                    <li>Start with <strong>cash today: {money2(data.starting_balance)}</strong> — the bank and cash account balances in your books.</li>
                    <li>Each day add the average of the last <strong>{a.run_rate_window_days} days</strong>: <strong>+{money2(a.avg_daily_income)}</strong> income and <strong>−{money2(a.avg_daily_expense)}</strong> expenses, i.e. <strong>{a.daily_net >= 0 ? "+" : "−"}{money2(Math.abs(a.daily_net))}/day</strong> net ({a.daily_net >= 0 ? "+" : "−"}{money2(Math.abs(runRate))} over {shownDays} days).</li>
                    <li>Add <strong>{money2(a.receivables_in_window)}</strong> of sent, unpaid invoices on their due dates ({a.receivables_rule}).</li>
                    <li>Subtract <strong>{money2(a.bills_in_window)}</strong> of bills on their due dates ({a.bills_rule}).</li>
                    <li>That gives <strong>{money2(matchesHeadline ? data.end_balance : computedEnd)}</strong> projected in {shownDays} days{matchesHeadline ? " — the same figure shown above (the daily rates here are rounded to the cent)" : ""}. Runway is the number of days until the projected balance reaches $0 at this pace; when it doesn't within a year we show 12+ months.</li>
                    <li>Status: <strong>shortfall risk</strong> if the projected low is below $0 · <strong>tight</strong> if it's below one month of expenses ({money2(a.avg_daily_expense * 30)}) · <strong>healthy</strong> otherwise.</li>
                  </ol>
                  <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>A run-rate projection assumes the recent past repeats. It can't see new customers, seasonality or one-off surprises — treat it as a planning estimate, not a prediction.</p>
                </details>
              );
            })()}
          </div>

          {/* Runway trend (last 6 months) */}
          {history?.has_history && (history.points || []).length >= 2 && (() => {
            const CAP = 120;
            const barData = (history.points || []).map((p) => ({
              ...p,
              value: p.positive ? CAP : Math.min(p.runway_days ?? 0, CAP),
            }));
            const barColor = (p) => {
              if (p.positive) return "var(--bk-success)";
              if (p.runway_days <= 30) return "var(--bk-energy)";
              if (p.runway_days <= 60) return "#B45309";
              return "var(--bk-success)";
            };
            return (
              <div className="bk-card p-5 bk-lift" data-testid="runway-history">
                <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Runway trend</h3>
                <p className="text-sm mb-3" style={{ color: "var(--bk-text-2)" }}>Projected runway as it stood at each month's close, recomputed from your ledger — is the cushion growing or shrinking?</p>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <BarChart data={barData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: "var(--bk-text-2)" }} axisLine={{ stroke: "var(--bk-border)" }} tickLine={false} />
                      <YAxis domain={[0, CAP + 10]} tickFormatter={(v) => (v >= CAP ? `${CAP}+` : v)} tick={{ fontSize: 11, fill: "var(--bk-text-2)" }} width={44} axisLine={false} tickLine={false} />
                      <Tooltip
                        cursor={{ fill: "var(--bk-page)" }}
                        formatter={(_v, _n, item) => [item?.payload?.positive ? "12+ months (projected)" : (item?.payload?.runway_days === 0 ? "At $0" : `~${item?.payload?.runway_days} days (projected)`), "Runway"]}
                        contentStyle={{ borderRadius: 12, border: "1px solid var(--bk-border)", fontSize: 13 }}
                      />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={48}>
                        {barData.map((p, i) => <Cell key={i} fill={barColor(p)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>Bars capped at 120+ mean the projection didn't reach $0 within a year at that month's pace.</p>
                {(history.milestone_log || []).length > 0 && (
                  <div className="mt-4 pt-3" style={{ borderTop: "1px dashed var(--bk-border)" }} data-testid="milestone-log">
                    <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--bk-text-2)" }}>Milestones earned — each one only once</p>
                    <div className="space-y-1.5">
                      {[...history.milestone_log].filter((m, i, arr) => arr.findIndex((x) => x.milestone === m.milestone) === i).reverse().map((m, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm" data-testid={`milestone-log-${idx}`}>
                          <Trophy size={15} weight="fill" color="var(--bk-success)" className="shrink-0" />
                          <span className="font-semibold" style={{ color: "var(--bk-text)" }}>
                            {m.milestone === 90 ? "90+ days" : `${m.milestone} days`} of projected runway
                          </span>
                          <span style={{ color: "var(--bk-text-2)" }}>
                            · {new Date(m.at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShareCard(m.milestone)}
                            data-testid={`milestone-share-${idx}`}
                            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors hover:opacity-80"
                            style={{ color: "var(--bk-accent)", border: "1px solid var(--bk-border)" }}
                          >
                            <ShareNetwork size={13} weight="bold" /> Share
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Scheduled bills */}
          <div className="bk-card p-5 bk-lift" data-testid="scheduled-bills">
            <div className="flex items-center justify-between mb-1">
              <div>
                <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Upcoming bills</h3>
                <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>Upcoming expenses — rent, taxes, big buys — so the forecast sees money going out too. Mark rent or software as recurring to repeat monthly.</p>
              </div>
              <button
                onClick={() => setShowBill((v) => !v)}
                data-testid="add-bill-toggle"
                className="bk-btn-primary shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2"
              >
                <Plus size={15} weight="bold" /> Add bill
              </button>
            </div>

            {showBill && (
              <div className="mt-3 bk-fade-up" data-testid="bill-form">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                  <div className="sm:col-span-4">
                    <label className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>What is it?</label>
                    <input value={billForm.name} onChange={(e) => setBillForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Q3 estimated taxes" data-testid="bill-name"
                      className="mt-1 w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>Amount ($)</label>
                    <input type="number" value={billForm.amount} onChange={(e) => setBillForm((f) => ({ ...f, amount: e.target.value }))}
                      placeholder="6000" data-testid="bill-amount"
                      className="mt-1 w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>{billForm.recurring ? "Starting" : "Due date"}</label>
                    <DateInput value={billForm.due_date} onChange={(e) => setBillForm((f) => ({ ...f, due_date: e.target.value }))}
                      data-testid="bill-date"
                      className="mt-1 w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                  </div>
                  <div className="sm:col-span-2">
                    <button onClick={addBill} disabled={savingBill} data-testid="bill-save"
                      className="bk-btn-primary w-full py-2 rounded-lg text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1.5">
                      {savingBill ? <CircleNotch size={14} className="animate-spin" /> : <CalendarPlus size={14} weight="bold" />} Save
                    </button>
                  </div>
                </div>
                <label className="mt-3 inline-flex items-center gap-2 text-sm cursor-pointer select-none" style={{ color: "var(--bk-text)" }} data-testid="bill-recurring-label">
                  <input type="checkbox" checked={billForm.recurring}
                    onChange={(e) => setBillForm((f) => ({ ...f, recurring: e.target.checked }))}
                    data-testid="bill-recurring" className="w-4 h-4 accent-[color:var(--bk-accent)]" />
                  Repeat monthly <span style={{ color: "var(--bk-text-2)" }}>(for rent, software & other regular bills)</span>
                </label>
              </div>
            )}

            <div className="mt-3">
              {(() => {
                const activeBills = bills.filter((x) => !x.paid);
                const paidBills = bills.filter((x) => x.paid);
                return (
                  <>
              {activeBills.length === 0 ? (
                <p className="text-sm py-3" style={{ color: "var(--bk-text-2)" }} data-testid="bills-empty">No upcoming bills logged. Add one to sharpen your forecast.</p>
              ) : (
                <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                  {activeBills.map((bl) => (
                    <div key={bl.id} className="py-2.5" data-testid={`bill-row-${bl.id}`}>
                    {editBill?.id === bl.id ? (
                      <div className="rounded-lg border p-3 bk-fade-up" style={{ borderColor: "var(--bk-accent)", background: "var(--bk-page)" }} data-testid={`bill-edit-form-${bl.id}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                          <div className="sm:col-span-4">
                            <label className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>Name</label>
                            <input value={editBill.name} onChange={(e) => setEditBill((f) => ({ ...f, name: e.target.value }))} data-testid={`bill-edit-name-${bl.id}`}
                              className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>Amount ($)</label>
                            <input type="number" value={editBill.amount} onChange={(e) => setEditBill((f) => ({ ...f, amount: e.target.value }))} data-testid={`bill-edit-amount-${bl.id}`}
                              className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                          </div>
                          <div className="sm:col-span-3">
                            <label className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>{editBill.recurring ? "Next due" : "Due date"}</label>
                            <DateInput value={editBill.due_date} onChange={(e) => setEditBill((f) => ({ ...f, due_date: e.target.value }))} data-testid={`bill-edit-date-${bl.id}`}
                              className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                          </div>
                          <div className="sm:col-span-3">
                            <label className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>Category (optional)</label>
                            <input value={editBill.category} onChange={(e) => setEditBill((f) => ({ ...f, category: e.target.value }))} placeholder="Rent & Lease" data-testid={`bill-edit-category-${bl.id}`}
                              className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                          </div>
                        </div>
                        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
                          <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none" style={{ color: "var(--bk-text)" }}>
                            <input type="checkbox" checked={editBill.recurring} onChange={(e) => setEditBill((f) => ({ ...f, recurring: e.target.checked }))} data-testid={`bill-edit-recurring-${bl.id}`} className="w-4 h-4 accent-[color:var(--bk-accent)]" />
                            Repeat monthly
                          </label>
                          <div className="flex gap-2">
                            <button onClick={() => setEditBill(null)} data-testid={`bill-edit-cancel-${bl.id}`} className="px-3 py-1.5 rounded-full border text-xs font-semibold bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>Cancel</button>
                            <button onClick={saveBillEdit} disabled={savingEdit} data-testid={`bill-edit-save-${bl.id}`} className="bk-btn-primary px-4 py-1.5 text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                              {savingEdit ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={12} weight="bold" />} Save changes
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
                          {bl.name}
                          {bl.recurring && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                              style={{ background: "var(--bk-accent-glow)", color: "var(--bk-accent)" }} data-testid={`bill-monthly-${bl.id}`}>MONTHLY</span>
                          )}
                        </p>
                        <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>{bl.recurring ? "every month from" : "due"} {dayLabel(bl.due_date)}{bl.category ? ` · ${bl.category}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                        <span className="text-sm font-bold" style={{ color: "var(--bk-energy)" }} data-testid={`bill-amount-${bl.id}`}>-{money2(bl.amount)}{bl.recurring ? "/mo" : ""}</span>
                        {bl.recurring ? (
                          <>
                            <button onClick={() => payRecurring(bl)} data-testid={`bill-pay-occurrence-${bl.id}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                              style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }} title="Log this month's payment to Books and roll to next month">
                              <CheckCircle size={13} weight="bold" /> Pay this month
                            </button>
                            {bl.last_payment && (
                              <button onClick={() => undoPayment(bl)} data-testid={`bill-undo-${bl.id}`}
                                className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                                style={{ color: "var(--bk-text-2)", border: "1px solid var(--bk-border)" }} title="Undo last payment">
                                <ArrowCounterClockwise size={13} weight="bold" /> Undo
                              </button>
                            )}
                          </>
                        ) : (
                          <button onClick={() => markBillPaid(bl)} data-testid={`bill-paid-${bl.id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                            style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }} title="Mark paid & log to Books">
                            <CheckCircle size={13} weight="bold" /> Paid
                          </button>
                        )}
                        <button onClick={() => setEditBill({ id: bl.id, name: bl.name, amount: String(bl.amount), due_date: bl.due_date, category: bl.category || "", recurring: !!bl.recurring })} data-testid={`bill-edit-${bl.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} title="Edit name, amount, date or schedule">
                          <PencilSimple size={13} weight="bold" /> Edit
                        </button>
                        <button onClick={() => deleteBill(bl)} data-testid={`bill-delete-${bl.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-energy)" }} title={bl.recurring ? "Stop this recurring bill" : "Remove bill"}>
                          <Trash size={13} weight="bold" /> Delete
                        </button>
                      </div>
                    </div>
                    )}
                    </div>
                  ))}
                </div>
              )}

              {paidBills.length > 0 && (
                <div className="mt-4 pt-3" style={{ borderTop: "1px dashed var(--bk-border)" }} data-testid="paid-bills">
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--bk-text-2)" }}>Recently paid</p>
                  <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                    {paidBills.map((bl) => (
                      <div key={bl.id} className="flex items-center justify-between gap-3 py-2" data-testid={`paid-bill-row-${bl.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm truncate" style={{ color: "var(--bk-text-2)" }}>
                            <CheckCircle size={13} weight="fill" color="var(--bk-success)" className="inline mr-1" />
                            {bl.name} <span className="text-xs">· logged to Books</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm font-semibold" style={{ color: "var(--bk-text-2)" }}>-{money2(bl.amount)}</span>
                          {bl.last_payment ? (
                            <button onClick={() => undoPayment(bl)} data-testid={`bill-undo-${bl.id}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                              style={{ color: "var(--bk-text-2)", border: "1px solid var(--bk-border)" }} title="Undo payment">
                              <ArrowCounterClockwise size={13} weight="bold" /> Undo
                            </button>
                          ) : (
                            <button onClick={() => deleteBill(bl)} data-testid={`bill-delete-${bl.id}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} aria-label="Remove bill">
                              <Trash size={13} weight="bold" /> Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
                  </>
                );
              })()}
            </div>
          </div>

          <p className="text-xs text-center" style={{ color: "var(--bk-text-2)" }}>
            Every number on this screen is a projection from your recent run-rate, sent invoices and scheduled bills — not a guarantee. It sharpens as you log more.
          </p>
        </>
      )}

      <MilestoneShareCard
        open={shareCard != null}
        milestone={shareCard}
        businessName={businessName}
        onClose={() => setShareCard(null)}
      />
    </div>
  );
}
